/**
 * Structured API error class for consistent error handling across the app.
 * Replaces ad-hoc error flags (error.isQueued, error.paymentRequired, etc.)
 */
export class ApiError extends Error {
  constructor(message, type = 'GENERIC', data = {}) {
    super(message)
    this.name = 'ApiError'
    this.type = type
    this.data = data
  }

  static TYPES = {
    QUEUED: 'QUEUED',
    PAYMENT_REQUIRED: 'PAYMENT_REQUIRED',
    RATE_LIMITED: 'RATE_LIMITED',
    OVERLOADED: 'OVERLOADED',
    DUPLICATE: 'DUPLICATE',
    LIMIT_REACHED: 'LIMIT_REACHED',
    NOT_FOUND: 'NOT_FOUND',
    VALIDATION: 'VALIDATION',
    TIMEOUT: 'TIMEOUT',
    GENERIC: 'GENERIC'
  }

  get isQueued() { return this.type === ApiError.TYPES.QUEUED }
  get isPaymentRequired() { return this.type === ApiError.TYPES.PAYMENT_REQUIRED }
  get isRateLimited() { return this.type === ApiError.TYPES.RATE_LIMITED }
  get isOverloaded() { return this.type === ApiError.TYPES.OVERLOADED }
  get isDuplicate() { return this.type === ApiError.TYPES.DUPLICATE }
  get isLimitReached() { return this.type === ApiError.TYPES.LIMIT_REACHED }
  get isNotFound() { return this.type === ApiError.TYPES.NOT_FOUND }
  get isTimeout() { return this.type === ApiError.TYPES.TIMEOUT }

  // Convenience factory methods
  static queued(message, data) {
    return new ApiError(message, ApiError.TYPES.QUEUED, data)
  }

  static paymentRequired(message, data) {
    return new ApiError(message, ApiError.TYPES.PAYMENT_REQUIRED, data)
  }

  static rateLimited(message) {
    return new ApiError(message, ApiError.TYPES.RATE_LIMITED)
  }

  static overloaded(message) {
    return new ApiError(message, ApiError.TYPES.OVERLOADED)
  }

  static duplicate(message, existingItems) {
    return new ApiError(message, ApiError.TYPES.DUPLICATE, { existingItems })
  }

  static limitReached(message) {
    return new ApiError(message, ApiError.TYPES.LIMIT_REACHED)
  }

  static timeout(message) {
    return new ApiError(message, ApiError.TYPES.TIMEOUT)
  }
}

/**
 * Helper to check if an error is an ApiError of a specific type.
 * Works with both new ApiError instances and legacy error objects with flags.
 */
export function isApiErrorType(error, type) {
  if (error instanceof ApiError) {
    return error.type === type
  }
  // Legacy support for old error flag patterns
  switch (type) {
    case ApiError.TYPES.QUEUED: return error?.isQueued === true
    case ApiError.TYPES.PAYMENT_REQUIRED: return error?.paymentRequired === true
    case ApiError.TYPES.RATE_LIMITED: return error?.isRateLimited === true
    case ApiError.TYPES.OVERLOADED: return error?.isOverloaded === true
    case ApiError.TYPES.DUPLICATE: return error?.isDuplicate === true
    case ApiError.TYPES.LIMIT_REACHED: return error?.isWardrobeLimitReached === true
    default: return false
  }
}
